import {
  AuffangraumFelder,
  AusstattungFelder,
  BetriebsmodusFelder,
  FGasFelder,
  StoffFelder,
} from "../services/api/anlagentypen";

/**
 * Typdefinitionen für das Windprojekt Kalkulator System
 */

/** Generische Auswahloption (value/label) für Options-Endpoints */
export interface SelectOption {
  value: string;
  label: string;
}

export interface Koordinate {
  laengengrad: number;
  breitengrad: number;
}

export interface GeoJSON {
  type: "Polygon" | "MultiPolygon";
  /** Koordinaten im GeoJSON-Format: [lon, lat] */
  coordinates: number[][][] | number[][][][];
}

export interface Area {
  id: string;
  project_id: string;
  /** GeoJSON-Geometrie der Windpark-Fläche (Polygon oder MultiPolygon) */
  geometrie?: GeoJSON;
  /** Beschreibung der Fläche (Markdown-Format oder Plain Text) */
  flaechenbeschreibung?: string | object;
  /** Breitengrad des Projektstandorts (WGS84) – manuell oder aus Zentroid der Geometrie */
  breitengrad?: number;
  /** Längengrad des Projektstandorts (WGS84) */
  laengengrad?: number;
}

/** Windenergieanlage – gehört zu genau einem Projekt */
/** Angaben, die es nur je Anlage gibt – der Anlagentyp kennt sie nicht */
// Die drei Feldgruppen werden beim Hinzufügen der Anlage vom Anlagentyp kopiert – sie sind
// dort definiert, damit die Vorlage und ihre Kopie nicht auseinanderlaufen.
export interface AnlageEigeneFelder {
  /** Steht erst nach der Bestellung fest, im Antrag regelmäßig leer (ELiA 16.1.9) */
  seriennummer?: string | null;
  /**
   * Ergebnis der Voranfrage bei der Bundesnetzagentur (ELiA 16.1.1). Nullbar mit Absicht:
   * „noch nicht angefragt" muss von „keine Richtfunkstrecke betroffen" unterscheidbar bleiben.
   */
  richtfunkstrecke_betroffen?: boolean | null;
  bnetza_vorgangsnummer?: string | null;
  /** Flächenbilanz des Betriebsgrundstücks je Anlage (ELiA 13.1.1) */
  flaeche_ueberbaut_vorhanden_m2?: number | null;
  flaeche_ueberbaut_zukuenftig_m2?: number | null;
  flaeche_zuwegung_vorhanden_m2?: number | null;
  flaeche_zuwegung_zukuenftig_m2?: number | null;
}

export interface GenerationUnit
  extends AuffangraumFelder, FGasFelder, AusstattungFelder, AnlageEigeneFelder {
  id: string;
  project_id: string;
  /** Anlagen-Nr. im Genehmigungsantrag, fortlaufend vergeben (0001, 0002 …) */
  anlagen_nr?: string;
  /** Betriebsinterne Bezeichnung im Antrag, z. B. "WEA 0001" */
  bezeichnung?: string;
  hersteller?: string;
  typ?: string;
  rotorradius?: number; // in m
  sicherungsradius?: number; // in m
  nennleistung?: number; // in MW
  referenzertrag?: number; // in MWh
  nabenhöhe?: number; // in m
  breitengrad?: number; // WGS84
  laengengrad?: number; // WGS84
  worst_parcel_status?: string; // Schlechtester Status der geschnittenen Flurstücke
}

export interface GenerationUnitCreate
  extends
    Partial<AuffangraumFelder>,
    Partial<FGasFelder>,
    Partial<AusstattungFelder>,
    AnlageEigeneFelder {
  anlagen_nr?: string;
  bezeichnung?: string;
  hersteller?: string;
  typ?: string;
  rotorradius?: number;
  sicherungsradius?: number;
  nennleistung?: number;
  referenzertrag?: number;
  nabenhöhe?: number;
  breitengrad?: number;
  laengengrad?: number;
  /**
   * Ersetzt die vom Anlagentyp kopierte Stoffliste. Nicht mitgesendet heißt „Vorlage
   * unverändert übernehmen", eine leere Liste dagegen ausdrücklich „keine Stoffe".
   * Beim Anlegen mehrerer Anlagen gilt die Angabe für alle.
   */
  stoffe?: Partial<StoffFelder>[];
  betriebsmodi?: Partial<BetriebsmodusFelder>[];
}

export type ErtragsprognoseTyp =
  | "Wind Pro"
  | "WindFarmer"
  | "Meteodyn WT"
  | "WindStation";
export type ScalerTyp = "EMD Default Meso Scaler" | "Anderer";

// Aktiver Ertragswert für Berechnungen (Bruttoertrag, P50, P75, P90)
export type AktiverErtragswert = "Bruttoertrag" | "P50" | "P75" | "P90";

/** Einzelne Abzugsposition innerhalb einer Ertragsprognose */
export interface DeductionItem {
  id: string;
  bezeichnung: string;
  /** Prozentwert (direkter Abzug) */
  wert: number;
  sort_order: number;
}

export interface DeductionItemCreate {
  bezeichnung: string;
  wert?: number;
}

export interface DeductionItemUpdate {
  bezeichnung?: string;
  wert?: number;
}

export interface Ertragsprognose {
  id: string;
  project_id: string;
  name?: string;
  bruttoertrag: number;
  nettoertrag_p50?: number;
  nettoertrag_p75?: number;
  nettoertrag_p90?: number;
  // Ertragsminderungsfaktoren (einzelne Abzüge)
  techn_verfuegbarkeit?: number; // in % (z.B. 95 = 95% Verfügbarkeit)
  abschalt_turbulenzen?: number; // in % (z.B. 6 = 6% Abschaltung)
  abschalt_sonstiges?: number; // in % (z.B. 3 = 3% Abschaltung)
  e_verluste?: number; // in % (z.B. 3 = 3% elektrische Verluste)
  // Berechneter Gesamtabzug (multiplikativ)
  abzuege_gesamt: number; // in % (automatisch berechnet)
  // Unsicherheit für P75/P90 Berechnung
  unsicherheit?: number; // in % (z.B. 14 = 14% Unsicherheit)
  wake_verlust?: number;
  kapazitaetsfaktor?: number;
  mittleres_wea_ergebnis?: number;
  windgeschwindigkeit_frei?: number;
  windgeschwindigkeit_reduziert?: number;
  // Gutachten-Informationen
  typ?: ErtragsprognoseTyp; // Optional (historische Daten haben evtl. Werte)
  erstellt_von: string;
  erstellt_am: string;
  aktiv: boolean;
  last_edited_by?: string;
  last_edited_at?: string;
  // Scaling-Informationen
  scaler_name: ScalerTyp;
  gelande_scaling: boolean;
  zeitraum_von?: string;
  zeitraum_bis?: string;
  meteo_objekte?: string;
  verdraengungshoehe?: string;
  deduction_items?: DeductionItem[];
}

export interface Windpark {
  id: string;
  name: string;
  /** Prognostizierter jährlicher Gewinn in Euro */
  gewinnProAnnum: number;
  /** Gesamtes Investitionsvolumen in Euro */
  investitionsvolumen: number;
  /** Eigenkapitalquote in Prozent (0-100) */
  ekQuote: number;
  /** Fremdkapitalzinssatz in Prozent */
  fkZins: number;
  /** Return on Investment in Prozent */
  roi: number;
  ertragsprognosen?: Ertragsprognose[];
  /** Flächen-Daten des Projekts (separat geladen) */
  area?: Area;

  // Kalkulationsparameter
  /** Gewerbesteuerhebesatz in Prozent */
  gewerbesteuerhebesatz?: number;
  /** Guthabenzins in Prozent (Standard: 2%) */
  guthabenzins?: number;
  /** Aktiver Ertragswert für Berechnungen (Bruttoertrag, P50, P75, P90) */
  aktiver_ertragswert?: AktiverErtragswert;
  /** Projektlaufzeit in Jahren (Standard: 20) */
  laufzeit_jahre?: number;

  // Stammdaten Projektrahmen
  /** Geplantes Datum des Baubeginns */
  baubeginn?: string;
  /** Geplantes Datum der Inbetriebnahme */
  inbetriebnahme?: string;
  /** Automatisch ermitteltes Bundesland (nicht editierbar) */
  bundesland?: string[];
  /** Automatisch ermittelter Landkreis/kreisfreie Stadt (nicht editierbar) */
  landkreis?: string[];
  /** Automatisch ermittelte Gemeinde (nicht editierbar) */
  gemeinde?: string[];
  /** Südregion gemäß § 36h EEG 2023 (Bayern/Baden-Württemberg) */
  ist_suedregion?: boolean;

  // Planungsdokumente (reine Dokumentation, ohne Einfluss auf Oberstatus/Score)
  regionalplan?: Planstatus;
  flaechennutzungsplan?: Planstatus;
  sonstiges_planungsverfahren?: Planstatus;

  // Projektstatus (Domänen-Status)
  planungsrecht_status?: string;
  /** Status des BImSchG-Genehmigungsverfahrens */
  bimschg_status?: string;
  /** Status der EEG-Ausschreibung */
  eeg_ausschreibung_status?: string;
  bau_status?: string;

  // DEPRECATED: Für Backwards Compatibility - werden aus area.* gelesen
  /** @deprecated Verwende area.geometrie */
  flaeche?: Koordinate[];
  /** @deprecated Verwende area.flaechenbeschreibung */
  flaechenbeschreibung?: string;
  /** @deprecated */
  flaeche_hektar?: number;
}

export interface WindparkListe {
  windparks: Windpark[];
}

// Planungsdokumente: Verfahrensstand (reine Dokumentation, siehe Planstatus im Backend)
export type Planstatus =
  | "Nicht erforderlich"
  | "Nicht begonnen"
  | "Aufstellungsbeschluss"
  | "Vorentwurf"
  | "Entwurf veröffentlicht"
  | "Erneute Beteiligung"
  | "Genehmigt";

/** Verfügbare Auswahlwerte für die Planungsdokumente (vom Backend) */
export interface PlanOptionsResponse {
  regionalplan_options: string[];
  flaechennutzungsplan_options: string[];
  sonstiges_planungsverfahren_options: string[];
}

export interface KPIData {
  anzahlProjekte: number;
  /** Durchschnittlicher IRR (Internal Rate of Return) in Prozent */
  durchschnittlicherIRR: number | null;
  /** Durchschnittliche Eigenkapitalquote in Prozent (aus Finanzierungen) */
  durchschnittlicheEkQuote: number | null;
  /** Erklärung des Wirtschaftlichkeits-Scores inkl. aktuell konfigurierter Gewichte */
  wirtschaftlichkeitErklaerung: string;
  /** Erklärung des Reifegrad-Scores inkl. aktuell konfigurierter Gewichte */
  reifegradErklaerung: string;
  /** Aktuell konfigurierte Gewichte der 6 Reifegrad-Domänen (org-weit) */
  reifegradGewichte: ReifegradGewichte | null;
}

export type PageType =
  | "overview"
  | "new-project"
  | "project-overview"
  | "project-company"
  | "project-permit-applications"
  | "project-permit-elia"
  | "project-permit-evidences"
  | "project-permit-costs"
  | "project-area"
  | "project-generation-units"
  | "project-yield"
  | "project-revenues"
  | "project-costs"
  | "project-financing"
  | "project-calculation";

/** @deprecated OverviewPage nutzt jetzt useOverviewData-Hook statt Props */
export interface OverviewPageProps {
  projects: ProjectOverviewItem[];
  kpis: KPIData;
  deadlines: DeadlineItem[];
  onNewProject: () => void;
  onProjectSelect?: (projectId: string) => void;
  onProjectDeleted?: (projectId: string) => void;
  error?: string | null;
}

export interface ProjectDetailPageProps {
  project: Windpark;
  onBack?: () => void;
  onTabChange?: (tabIndex: number) => void;
  currentTab?: number;
}

export interface KPICardProps {
  title: string;
  value: string;
  /** Optionale Farbe für Wert und Balken */
  color?: string;
  /** Zeigt einen farbigen Balken am unteren Rand (benötigt color) */
  showColorBar?: boolean;
}

/**
 * Unterzeile eines aufgeklappten Projekts, etwa ein Antrag. Ihre Zellen stehen unter den
 * Spalten des Projekts; die Gruppe ist die Statuskategorie, zu der sie gehört, und erscheint als
 * Icon vor ihrer ersten Unterzeile.
 */
export interface ProjectSubRow {
  id: string;
  group: { key: string; label: string; icon: React.ReactNode };
  /** Inhalt je Spalte des Projekts (`projekt`, `targetDate`, `gesamtstatus`, `reifegrad`, `wirtschaftlichkeit`) */
  cells: Partial<Record<string, React.ReactNode>>;
  /** Inhalt der Aktionsspalte, etwa ein Drei-Punkte-Menü */
  actions?: React.ReactNode;
}

export interface ProjectTableProps {
  projects: ProjectOverviewItem[];
  onProjectSelect?: (projectId: string) => void;
  /**
   * Öffnet das Projekt über den Namen als Link. Mit `onExpand` wählt ein Klick auf die Zeile
   * aus und klappt sie auf, statt das Projekt zu öffnen.
   */
  onProjectOpen?: (projectId: string) => void;
  /** Aufgeklapptes Projekt – höchstens eines */
  expandedId?: string | null;
  /** Klappt ein Projekt auf bzw. mit `null` zu; macht die Zeilen aufklappbar */
  onExpand?: (projectId: string | null) => void;
  /** Inhalt der aufgeklappten Zeile unterhalb der Unterzeilen; `null` für keinen */
  renderExpandedRow?: (projectId: string) => React.ReactNode;
  /** Unterzeilen des aufgeklappten Projekts in den Spalten der Tabelle */
  subRows?: (projectId: string) => ProjectSubRow[];
  /** Steht direkt hinter dem Projektnamen, z. B. die Problemsignale */
  renderNameSuffix?: (projectId: string) => React.ReactNode;
  /** Höhe des Scrollbereichs; mit Angabe scrollt die Tabelle selbst unter festem Kopf */
  maxHeight?: string;
  onProjectDeleted?: (projectId: string) => Promise<void>;
  onProjectExport?: (projectId: string) => Promise<void>;
  exportingProjectId?: string | null;
  /** Öffnet die Zugriffsverwaltung. Ohne Angabe erscheint kein Teilen-Icon. */
  onProjectShare?: (projectId: string) => void;
  /**
   * Welche Spalten angezeigt werden dürfen. Wird außerhalb ermittelt, damit die Tabelle
   * fertige Werte bekommt und nichts über Bereiche wissen muss.
   */
  spaltenSichtbar?: {
    /** IRR, Investition, EK-Quote – hängt an `kalkulation.wirtschaftlichkeit` */
    wirtschaftlichkeit: boolean;
    /** Gesamtstatus und Reifegrad fassen alle Bereiche zusammen – nur für Interne */
    aggregate: boolean;
  };
  selectedId?: string;
  /**
   * IDs der Projekte, deren Werte aus einer nicht gespeicherten Simulation stammen.
   * Zusammen mit `simulierteSpalten` bestimmt das, welche Zellen abgesetzt dargestellt
   * werden – die Tabelle entscheidet das nicht selbst, sie bekommt es fertig geliefert.
   */
  simulierteIds?: Set<string>;
  /** Schlüssel der Spalten, deren Werte aus der Simulation stammen */
  simulierteSpalten?: Set<string>;
  /** Erklärung des Wirtschaftlichkeits-Scores inkl. aktuell konfigurierter Gewichte (organisationsabhängig) */
  wirtschaftlichkeitErklaerung?: string;
  /** Erklärung des Reifegrad-Scores inkl. aktuell konfigurierter Gewichte (organisationsabhängig) */
  reifegradErklaerung?: string;
  /** Aktuell konfigurierte Gewichte der 6 Reifegrad-Domänen (org-weit) */
  reifegradGewichte?: ReifegradGewichte | null;
}

export interface NewProjectFormData {
  name: string;
  // flaeche wird NICHT mehr im Form State gehalten
  // Sie wird direkt an submitForm übergeben und separat gespeichert
}

export interface ValidationErrors {
  [field: string]: string;
}

/** Unterabschnitte für Investitionskosten (fix vorgegeben) */
export type CapexSection =
  | "windenergieanlage"
  | "infrastruktur"
  | "planung_genehmigung"
  | "genehmigung"
  | "bauueberwachung";

export const CAPEX_SECTION_LABELS: Record<CapexSection, string> = {
  windenergieanlage: "Windenergieanlage",
  infrastruktur: "Infrastruktur",
  planung_genehmigung: "Planung",
  genehmigung: "Genehmigung",
  bauueberwachung: "Bauüberwachung",
};

/**
 * Reihenfolge der CAPEX-Sections mit eigenen Positionen.
 *
 * "genehmigung" fehlt hier bewusst: der Abschnitt hat keine `capex_items`, sondern wird aus
 * dem Genehmigungsverfahren abgeleitet (`Capex.genehmigung`) und deshalb separat gerendert.
 */
export const CAPEX_SECTION_ORDER: Exclude<CapexSection, "genehmigung">[] = [
  "windenergieanlage",
  "infrastruktur",
  "planung_genehmigung",
  "bauueberwachung",
];

export interface CapexItem {
  id: string;
  capex_id: string;
  section: CapexSection;
  bezeichnung: string;
  /** Wert in EUR (immer Gesamtkosten) */
  wert: number;
  /** Abschreibungsdauer in Jahren */
  abschreibungsdauer: number;
  /** True = Eingabe als Kosten pro Anlage (Frontend rechnet um) */
  is_per_unit: boolean;
  /** Ob Position umbenannt/gelöscht werden kann */
  is_editable: boolean;
  sort_order: number;
  created_at: string;
  updated_at?: string;
}

export interface CapexItemCreate {
  section: CapexSection;
  bezeichnung: string;
  wert?: number;
  abschreibungsdauer?: number;
  is_per_unit?: boolean;
}

export interface CapexItemUpdate {
  bezeichnung?: string;
  wert?: number;
  abschreibungsdauer?: number;
  is_per_unit?: boolean;
}

/** Eine aus dem Genehmigungsverfahren abgeleitete Zeile des Genehmigungs-Abschnitts (read-only) */
export interface GenehmigungCostLine {
  bezeichnung: string;
  /** Geplante Kosten in EUR (netto) */
  wert: number;
  permit_procedure_id: string;
  /** Für die Gruppierung, wenn ein Projekt mehrere Verfahren hat */
  permit_procedure_bezeichnung?: string;
  evidence_id?: string;
}

/**
 * Abgeleiteter CAPEX-Abschnitt "Genehmigung".
 *
 * `summe` ist der Wert, mit dem die Kalkulation rechnet – die Übersteuerung, falls gesetzt,
 * sonst `summe_abgeleitet`. Beide werden geliefert, damit die UI im übersteuerten Zustand
 * anbieten kann, wieder auf die Planwerte aus dem Verfahren zurückzugehen.
 */
export interface GenehmigungSection {
  summe: number;
  summe_abgeleitet: number;
  ist_uebersteuert: boolean;
  items: GenehmigungCostLine[];
}

export interface Capex {
  id: string;
  project_id: string;

  /** Alle CAPEX-Positionen – ohne die des abgeleiteten Genehmigungs-Abschnitts */
  items: CapexItem[];

  /** Mandanten-spezifische Section-Labels (überschreiben CAPEX_SECTION_LABELS) */
  section_labels?: Record<string, string>;

  /** Abgeleiteter Abschnitt "Genehmigung" aus der Permits-Domäne */
  genehmigung?: GenehmigungSection;

  // Berechnete Summen pro Section (vom Backend)
  summe_windenergieanlage?: number;
  summe_infrastruktur?: number;
  summe_planung_genehmigung?: number;
  summe_genehmigung?: number;
  summe_bauueberwachung?: number;
  summe_gesamt?: number;

  created_at: string;
  updated_at?: string;
}

/** @deprecated Nicht mehr verwendet - Items werden einzeln aktualisiert */
export interface CapexUpdate {}

/** Eingabemodus für Betriebskostenarten */
export type CostInputMode = "steigend" | "manuell" | "prozent";

/** Unterabschnitte für Betriebskosten (fix vorgegeben) */
export type OpexSection =
  | "pachtkosten"
  | "wartung"
  | "versicherungen"
  | "banken"
  | "sonstige_abgaben";

export const OPEX_SECTION_LABELS: Record<OpexSection, string> = {
  pachtkosten: "Pachtkosten",
  wartung: "Wartung",
  versicherungen: "Versicherungen",
  banken: "Banken",
  sonstige_abgaben: "Sonstige Abgaben",
};

export const OPEX_SECTION_ORDER: OpexSection[] = [
  "pachtkosten",
  "wartung",
  "versicherungen",
  "banken",
  "sonstige_abgaben",
];

/** @deprecated Wird durch OpexSection ersetzt */
export type CostType =
  | "pacht"
  | "wartungsvertrag"
  | "monitoring"
  | "versicherung_haftpflicht"
  | "betriebsunterbrechung"
  | "bank_gebuehren"
  | "bank_buergschaft"
  | "sonstige";

/** Manuelle Jahreswerte für eine Kostenposition (User-Eingaben bei Modus 'manuell') */
export interface ManualCostValue {
  id: string;
  cost_item_id: string;
  jahr: number;
  wert: number;
  created_at: string;
  updated_at?: string;
}

/**
 * Pachtkostenstaffel für gestaffelte Pachtkosten
 *
 * Beispiel: Jahr 1-6: 8%, Jahr 7-12: 10%, Jahr 13-20: 12%
 *
 * Logik:
 * - Erste Staffel hat immer first_year = 1 (nicht editierbar im UI)
 * - Das letzte Jahr (last_year) ergibt sich aus next_tier.first_year - 1
 * - Die letzte Staffel endet automatisch bei projekt.laufzeit_jahre
 */
export interface LeaseTier {
  id: string;
  cost_item_id: string;
  /** Erstes Jahr dieser Staffel (1-50) */
  first_year: number;
  /** Prozentsatz vom Ertrag (z.B. 8.0 für 8%) */
  percentage: number;
  created_at: string;
  updated_at?: string;
}

export interface LeaseTierCreate {
  first_year: number;
  percentage: number;
}

export interface LeaseTierUpdate {
  first_year?: number;
  percentage?: number;
}

/**
 * Einzelne OPEX-Position mit Modus und Steigerung
 *
 * Bei Modus "prozent":
 * - prozent_satz enthält die User-Eingabe in %
 * - basiswert wird on-the-fly vom Backend berechnet (EUR Jahr 1)
 */
export interface OpexItem {
  id: string;
  opex_id: string;
  section: OpexSection;
  bezeichnung: string;
  /** Basiswert in EUR/Jahr (bei Modus "prozent": vom Backend berechneter Wert) */
  basiswert: number;
  /** Eingabemodus: fix, steigend, manuell, prozent */
  modus: CostInputMode;
  /** Jährliche Steigerungsrate in % (bei Modus 'steigend'), Default: 2.0 */
  steigerung: number;
  /** Prozentsatz vom Ertrag (nur bei Modus "prozent") */
  prozent_satz?: number | null;
  /** Ob Position umbenannt/gelöscht werden kann */
  is_editable: boolean;
  sort_order: number;
  /** Manuelle Jahreswerte (nur bei Modus 'manuell') */
  manual_values: ManualCostValue[];
  /** Pachtkostenstaffeln (nur bei Pachtkosten) */
  lease_tiers?: LeaseTier[];
  created_at: string;
  updated_at?: string;
}

export interface OpexItemCreate {
  section: OpexSection;
  bezeichnung: string;
  basiswert?: number;
  modus?: CostInputMode;
  steigerung?: number; // Default: 2.0
  prozent_satz?: number | null;
}

export interface OpexItemUpdate {
  bezeichnung?: string;
  basiswert?: number;
  modus?: CostInputMode;
  steigerung?: number;
  prozent_satz?: number | null;
}

/** @deprecated Verwendet OpexItem stattdessen */
export type CostItem = OpexItem;
/** @deprecated Verwendet OpexItemUpdate stattdessen */
export type CostItemUpdate = OpexItemUpdate;

export interface Opex {
  id: string;
  project_id: string;

  items: OpexItem[];

  /** Mandanten-spezifische Section-Labels (überschreiben OPEX_SECTION_LABELS) */
  section_labels?: Record<string, string>;

  // Berechnete Summen pro Section (vom Backend)
  summe_pachtkosten?: number;
  summe_wartung?: number;
  summe_versicherungen?: number;
  summe_banken?: number;
  summe_sonstige_abgaben?: number;
  summe_gesamt?: number;

  created_at: string;
  updated_at?: string;
}

export interface OpexUpdate {}

/** Berechnete Kosten für ein einzelnes Jahr */
export interface YearlyCostEntry {
  jahr: number;
  /** Kosten pro Item-ID oder Kostenart */
  kosten: Record<string, number>;
  /** Summe aller Betriebskosten in EUR */
  summe_betriebskosten: number;
  /** Abschreibungen in EUR */
  abschreibungen: number;
  /** Welche Kosten-Keys editierbar sind (Modus=manuell) */
  is_editable: Record<string, boolean>;
}

/** Response für die berechnete 20-Jahres-Übersicht */
export interface YearlyCostsResponse {
  jahre: YearlyCostEntry[];
  summe_betriebskosten_gesamt: number;
  summe_abschreibungen_gesamt: number;
}

export interface ManualCostValueUpdate {
  wert: number;
}

export interface ProjectCosts {
  capex?: Capex;
  opex?: Opex;
}

export interface EEGTariff {
  id: string;
  project_id: string;

  /** Zuschlagswert in Cent/kWh (Pflichtfeld für Berechnungen) */
  zuschlagswert?: number;

  // Berechnete Felder (on-demand, nicht in DB gespeichert)
  /** Berechneter Referenzertrag in MWh (Summe aller Anlagen-Referenzerträge) */
  referenzertrag?: number;
  /** Berechnete Standortgüte in % (P50 / Referenzertrag * 100) */
  standortguete?: number;
  /** Berechneter Korrekturfaktor aus Stützpunkttabelle (gemäß § 36h Abs. 1 EEG 2023) */
  korrekturfaktor?: number;

  created_at: string;
  updated_at?: string;
}

/**
 * Schema zum Aktualisieren eines EEG-Tarifs.
 * referenzertrag, standortguete und korrekturfaktor sind nicht editierbar (werden berechnet).
 */
export interface EEGTariffUpdate {
  /** Zuschlagswert in Cent/kWh */
  zuschlagswert?: number;
}

export interface MarktpreisTariff {
  id: string;
  project_id: string;

  /** Verkaufspreis in Cent/kWh für Jahre nach EEG-Zeitraum (Default: 6 Cent) */
  marktpreis_kwh: number;
  /** Einkaufspreis in Cent/kWh, z.B. für PPA-Zukauf */
  einkaufspreis_kwh?: number;
  /** Kommunale Beteiligung § 6 EEG in Cent/kWh */
  kommunale_beteiligung_kwh?: number;

  created_at: string;
  updated_at?: string;
}

export interface MarktpreisTariffUpdate {
  /** Verkaufspreis in Cent/kWh für Jahre nach EEG-Zeitraum */
  marktpreis_kwh?: number;
  /** Einkaufspreis in Cent/kWh, z.B. für PPA-Zukauf */
  einkaufspreis_kwh?: number;
  /** Kommunale Beteiligung § 6 EEG in Cent/kWh */
  kommunale_beteiligung_kwh?: number;
}

export interface PPATariff {
  id: string;
  project_id: string;

  bezeichnung: string;
  /** Startjahr (1 - Projektlaufzeit) */
  beginn_jahr: number;
  /** Endjahr (1 - Projektlaufzeit) */
  ende_jahr: number;
  /** Preis in Cent/kWh */
  preis_kwh: number;
  /** Abnahmemenge in kWh pro Jahr */
  abnahmemenge_kwh_jahr: number;

  created_at: string;
  updated_at?: string;
}

export interface PPATariffCreate {
  bezeichnung: string;
  /** Startjahr (1 - Projektlaufzeit) */
  beginn_jahr: number;
  /** Endjahr (1 - Projektlaufzeit) */
  ende_jahr: number;
  /** Preis in Cent/kWh */
  preis_kwh: number;
  /** Abnahmemenge in kWh pro Jahr */
  abnahmemenge_kwh_jahr: number;
}

export interface PPATariffUpdate {
  bezeichnung?: string;
  /** Startjahr (1 - Projektlaufzeit) */
  beginn_jahr?: number;
  /** Endjahr (1 - Projektlaufzeit) */
  ende_jahr?: number;
  /** Preis in Cent/kWh */
  preis_kwh?: number;
  /** Abnahmemenge in kWh pro Jahr */
  abnahmemenge_kwh_jahr?: number;
}

/**
 * Detaillierter Erlös eines einzelnen PPA-Vertrags für ein Jahr.
 * Ermöglicht die Anzeige individueller PPA-Erlöse im Tooltip.
 */
export interface PPARevenueDetail {
  ppa_id: string;
  bezeichnung: string;
  /** Erlös aus diesem PPA in EUR */
  erloes_eur: number;
  /** Abgenommene Menge in kWh */
  menge_kwh: number;
  /** Preis in Cent/kWh */
  preis_cent: number;
}

/**
 * Einzelner Jahreseintrag für Energieerlös mit Aufschlüsselung nach Tarifquelle.
 *
 * Berechnung:
 * 1. PPA-Erlöse werden zuerst berechnet (Abnahmemenge × PPA-Preis)
 * 2. Verbleibende kWh werden mit EEG (Jahr 1-20) oder Marktpreis (ab Jahr 21) verrechnet
 */
export interface EnergyRevenueEntry {
  /** Jahr (1-50) */
  jahr: number;
  /** Erlös aus EEG-Tarif in EUR */
  erloes_eeg: number;
  /** Erlös aus PPA-Verträgen in EUR (Summe aller PPAs) */
  erloes_ppa: number;
  /** Erlös aus Marktpreis in EUR (ab Jahr 21) */
  erloes_marktpreis: number;
  /** Individuelle Erlöse pro PPA-Vertrag (für Tooltip) */
  ppa_details: PPARevenueDetail[];
  /** Brutto-Gesamterlös in EUR (EEG + PPA + Marktpreis) */
  erloes_eur: number;
  /** Kommunale Beteiligung in EUR */
  abzug_kommunal: number;
  /** PPA-Zukaufskosten in EUR */
  zukauf_ppa: number;
  /** Netto-Erlös = Brutto - Abzüge */
  netto_erloes_eur: number;
}

export interface EnergyRevenuesResponse {
  project_id: string;
  eintraege: EnergyRevenueEntry[];
  /** Summe aller Energieerlöse in EUR */
  summe_gesamt: number;
  /** Verwendeter Ertragstyp (Bruttoertrag, P50, P75, P90) */
  aktiver_ertragswert_typ?: string;
  /** Ertragswert in MWh */
  ertragswert_mwh?: number;
  /** Abzüge in Prozent */
  abzuege_prozent?: number;
  /** Zuschlagswert in Cent/kWh */
  zuschlagswert_cent?: number;
  /** Zeitpunkt der Berechnung */
  created_at?: string;
}

export interface CalculationParametersUpdate {
  gewerbesteuerhebesatz?: number;
  guthabenzins?: number;
  aktiver_ertragswert?: AktiverErtragswert;
  laufzeit_jahre?: number;
}

export interface ProfitAndLoss {
  id: string;
  project_id: string;

  jahr: number;

  jahresenergieerloes: number;
  zinserloes: number;
  gesamterloes: number;

  laufender_aufwand: number;
  afa: number;
  zinsen: number;
  gesamtaufwand: number;

  ergebnis_vor_steuern: number;

  // Gewerbesteuer-Zwischenwerte (vom Backend berechnet)
  /** Hinzurechnung: Zinsen / 2 */
  hinzurechnung: number;
  /** Maßgebender Gewinnertrag: Ergebnis vor Steuern + Hinzurechnung */
  massgebender_gewinnertrag: number;
  /** Verlustvortrag aus Vorjahren */
  verlustvortrag: number;
  /** Verwendeter Freibetrag (0 bis max. 24.000€) */
  gewerbe_freibetrag_verwendet: number;
  /** Zu versteuernder Ertrag: Maßgebender Gewinnertrag - Verlustvortrag - Freibetrag */
  zu_versteuernder_ertrag: number;

  gewerbesteuer: number;
  ergebnis_nach_steuern: number;

  created_at: string;
  updated_at?: string;
}

export interface ProfitAndLossCalculateParams {
  laufzeit_jahre?: number;
  speichern?: boolean;
}

export interface CashflowEntry {
  /** Jahr der Berechnung (1, 2, 3, ...) */
  jahr: number;
  /** Energieerlös in EUR */
  energieerloes: number;
  /** Zinserlös in EUR */
  zinserloes: number;
  /** Gesamterlös in EUR (Energieerlös + Zinserlös) */
  gesamterloes: number;
  /** Laufende Betriebskosten in EUR */
  laufender_aufwand: number;
  /** Ergebnis vor Steuern in EUR (aus GuV) */
  ergebnis_vor_steuern: number;
  /** Abschreibungen (AfA) in EUR */
  abschreibungen: number;
  /** Zinsaufwand in EUR */
  zinsen: number;
  /** Cashflow vor Schuldendienst in EUR */
  cfads: number;
  /** DSCR = CFADS / (Tilgung + Zinsen). Werte > 1 bedeuten Schuldendienstfähigkeit */
  dscr: number;
  /** Tilgungsbetrag in EUR */
  tilgung: number;
  /** Cashflow nach Schuldendienst (vor Steuern) in EUR */
  cashflow_nach_schuldendienst: number;
  /** Kumulierter Liquiditätsbestand in EUR */
  liquiditaetsbestand: number;
}

export interface CashflowResponse {
  entries: CashflowEntry[];
  /** Gesamtsumme aller Cashflows (Liquiditätsbestand am Ende) */
  summe_cashflow: number;
  /** Minimaler DSCR über alle Jahre (kritischster Wert) */
  min_dscr?: number;
  /** Durchschnittlicher DSCR über alle Jahre */
  avg_dscr?: number;
  /** Eigenkapital-IRR (Internal Rate of Return) in Prozent */
  irr?: number;
}

export interface CashflowCalculateParams {
  laufzeit_jahre?: number;
}

/**
 * Combined Response für die Kalkulationsseite (GuV + Cashflow in einem Request).
 * Vermeidet doppelte GuV-Berechnung und reduziert API-Aufrufe.
 */
export interface CalculationPageResponse {
  /** Gespeicherte GuV-Einträge pro Jahr (mit IDs) */
  guv_entries: ProfitAndLoss[];
  /** Cashflow-Berechnung mit KPIs (IRR, DSCR) */
  cashflow: CashflowResponse;
}

export type TilgungsIntervall = "jaehrlich" | "quartalsweise" | "monatlich";

export type DarlehensTyp = string;

export interface Financing {
  id: string;
  project_id: string;

  finanzierung_status?: string;
  /** Eigenkapitalquote in Prozent (z.B. 20 für 20%) */
  eigenkapitalquote?: number;

  created_at: string;
  updated_at?: string;
}

export interface FinancingUpdate {
  eigenkapitalquote?: number;
  finanzierung_status?: string;
}

export interface Loan {
  id: string;
  financing_id: string;

  /** Typ des Darlehens (Bankdarlehen, Förderdarlehen oder Bürgerbeteiligung) */
  typ: DarlehensTyp;
  /** Darlehenshöhe in EUR */
  darlehenshoehe?: number;
  /** Fremdkapitalzins in Prozent pro Jahr (z.B. 5 für 5%) */
  fremdkapitalzins?: number;
  /** Laufzeit des Darlehens in Jahren */
  laufzeit_jahre?: number;
  /** Tilgungsfreie Monate am Anfang (0 = keine tilgungsfreie Zeit) */
  tilgungsfreie_monate?: number;
  /** Tilgungsintervall (jährlich, quartalsweise, monatlich) */
  tilgungsintervall?: TilgungsIntervall;
  /** Kennzeichnung als Hauptdarlehen. Das erste Darlehen wird automatisch als Hauptdarlehen gesetzt. */
  is_hauptdarlehen?: boolean;
  /** Monate relativ zum Betriebsbeginn. Positiv = vorher, Negativ = nachher, 0 = bei Betriebsbeginn */
  beginn_monate_relativ_betrieb?: number;
  /** Bereitstellungszins in % p.a. für noch nicht ausgezahlte Beträge */
  bereitstellungszins?: number;

  created_at: string;
  updated_at?: string;
}

export interface LoanCreate {
  typ?: DarlehensTyp;
  darlehenshoehe?: number;
  fremdkapitalzins?: number;
  laufzeit_jahre?: number;
  tilgungsfreie_monate?: number;
  tilgungsintervall?: TilgungsIntervall;
  is_hauptdarlehen?: boolean;
  beginn_monate_relativ_betrieb?: number;
  bereitstellungszins?: number;
}

export interface LoanUpdate {
  typ?: DarlehensTyp;
  darlehenshoehe?: number;
  fremdkapitalzins?: number;
  laufzeit_jahre?: number;
  tilgungsfreie_monate?: number;
  tilgungsintervall?: TilgungsIntervall;
  is_hauptdarlehen?: boolean;
  beginn_monate_relativ_betrieb?: number;
  bereitstellungszins?: number;
}

export interface RepaymentPlanEntry {
  id: string;
  loan_id: string;

  /** Betriebsjahr (-3, -2, -1, 1, 2, 3, ...) - kein Jahr 0 */
  betriebsjahr: number;
  /** Monat innerhalb des Jahres (1-12) */
  monat: number;
  /** Auszahlung des Darlehens in diesem Monat in EUR */
  auszahlung: number;
  /** Kreditzinsen auf ausgezahlten Betrag in EUR */
  kreditzins_betrag: number;
  /** Bereitstellungszinsen auf nicht ausgezahlten Betrag in EUR */
  bereitstellungszins_betrag: number;
  /** Tilgungsbetrag in EUR */
  tilgungsbetrag: number;
  /** Restschuld am Monatsende in EUR */
  restschuld: number;

  created_at: string;
  updated_at?: string;
}

/** Monatliche Basis-Daten ohne ID/Metadaten */
export interface MonthlyRepaymentEntryBase {
  betriebsjahr: number;
  monat: number;
  auszahlung: number;
  kreditzins_betrag: number;
  bereitstellungszins_betrag: number;
  tilgungsbetrag: number;
  restschuld: number;
}

export interface AggregatedYearEntry {
  /** Betriebsjahr (-3, -2, -1, 1, 2, 3, ...) */
  betriebsjahr: number;
  /** Summe Auszahlungen in EUR */
  auszahlung_summe: number;
  /** Summe Kreditzinsen in EUR */
  kreditzins_summe: number;
  /** Summe Bereitstellungszinsen in EUR */
  bereitstellungszins_summe: number;
  /** Summe aller Zinsen (Kredit + Bereitstellung) in EUR */
  zinsbetrag_summe: number;
  /** Summe Tilgung in EUR */
  tilgungsbetrag_summe: number;
  /** Restschuld am Jahresende in EUR */
  restschuld_jahresende: number;
  /** Monatliche Details für aufklappbare Spalten */
  monate: MonthlyRepaymentEntryBase[];
}

/** Auszahlungsdaten eines einzelnen Darlehens pro Jahr */
export interface LoanYearlyAuszahlung {
  loan_id: string;
  /** Bezeichnung des Darlehens (Typ + Höhe) */
  loan_bezeichnung: string;
  betriebsjahr: number;
  /** Summe Auszahlungen im Jahr */
  auszahlung_summe: number;
  /** Monatswerte für Auszahlungen (nur aktive Monate des Jahres) */
  monate: number[];
  /** Ob die Auszahlungen in diesem Jahr für dieses Darlehen editierbar sind */
  ist_editierbar: boolean;
}

export interface LoanYearlyZinsen {
  loan_id: string;
  loan_bezeichnung: string;
  betriebsjahr: number;
  kreditzins_summe: number;
  bereitstellungszins_summe: number;
}

/** Details für die aufklappbare Zinsen-Zeile */
export interface ZinsenDetails {
  betriebsjahr: number;
  kreditzins_summe: number;
  bereitstellungszins_summe: number;
  loans: LoanYearlyZinsen[];
}

export interface LoanValidierungsfehler {
  loan_id: string;
  loan_bezeichnung: string;
  fehler: string;
  auszahlung_summe: number;
  darlehenshoehe: number;
  differenz: number;
}

export interface AggregatedRepaymentPlanResponse {
  /** Aggregierte Jahresdaten (sortiert nach Betriebsjahr) */
  jahre: AggregatedYearEntry[];
  /** Auszahlungen pro Darlehen und Jahr (für aufklappbare Auszahlungs-Zeile) */
  auszahlungen_pro_darlehen: LoanYearlyAuszahlung[];
  /** Zinsen-Details pro Jahr (für aufklappbare Zinsen-Zeile) */
  zinsen_details: ZinsenDetails[];
  /** Validierungsfehler pro Darlehen (Auszahlung != Darlehenshöhe) */
  validierung_fehler: LoanValidierungsfehler[];
  /** Frühestes Betriebsjahr (kann negativ sein) */
  min_betriebsjahr: number;
  /** Spätestes Betriebsjahr */
  max_betriebsjahr: number;
}

/** Legacy-Format für Abwärtskompatibilität */
export interface AggregatedRepaymentPlanEntry {
  /** Betriebsjahr */
  jahr: number;
  /** Summe Zinsbetrag aller Loans in EUR */
  zinsbetrag: number;
  /** Summe Tilgungsbetrag aller Loans in EUR */
  tilgungsbetrag: number;
  /** Summe Restschuld aller Loans am Jahresende in EUR */
  restschuld: number;
}

export interface AuszahlungUpdate {
  betriebsjahr: number;
  monat: number;
  auszahlung: number;
}

export interface AuszahlungUpdateResponse {
  success: boolean;
  message?: string;
  validierung_fehler?: LoanValidierungsfehler;
  updated_entry?: RepaymentPlanEntry;
}

/**
 * Ein einzelner Auszahlungspunkt im Auszahlungsplan.
 * Repräsentiert einen Zeitpunkt an dem ein Teil des Darlehens ausgezahlt wird.
 */
export interface AuszahlungEntry {
  betriebsjahr: number;
  monat: number;
  betrag: number;
  /** True wenn dies die Hauptauszahlung (Abruf-Zeitpunkt) ist - nicht löschbar */
  ist_hauptauszahlung: boolean;
}

export interface AuszahlungenResponse {
  entries: AuszahlungEntry[];
  summe: number;
  darlehenshoehe: number;
  ist_vollstaendig: boolean;
  bereitstellungszins: number;
}

export interface AuszahlungenBulkUpdate {
  entries: AuszahlungEntry[];
  bereitstellungszins?: number;
}

export interface LoanWithPlan extends Loan {
  /** Liste der monatlichen Tilgungsplan-Einträge (sortiert nach Betriebsjahr und Monat) */
  repayment_plan: RepaymentPlanEntry[];
}

/**
 * Response-Type für Loan-CRUD-Operationen mit Validierungen.
 * Enthält das erstellte/aktualisierte Darlehen und domänenübergreifende Validierungen.
 */
export interface LoanWithPlanResponse {
  data: LoanWithPlan;
  validations: ValidationsResponse;
}

/**
 * Response für das Löschen eines Darlehens.
 * Enthält nur Validierungen (kein data, da das Darlehen gelöscht wurde).
 */
export interface LoanDeleteResponse {
  validations: ValidationsResponse;
}

export interface FinancingWithLoans extends Financing {
  /** Liste der Darlehen mit jeweiligem monatlichem Tilgungsplan */
  loans: LoanWithPlan[];
}

export interface CalculateRepaymentPlanParams {
  speichern?: boolean;
}

/**
 * Vorgeschlagene Darlehenshöhe für ein neues Darlehen.
 * Wird verwendet, um beim Anlegen eines neuen Darlehens
 * die Darlehenshöhe automatisch vorzuschlagen.
 */
export interface SuggestedLoanAmount {
  /** Vorgeschlagene Darlehenshöhe in EUR */
  suggested_amount: number;
  /** Gesamtes Fremdkapital (FK) in EUR */
  fremdkapital: number;
  /** Summe aller bestehenden Darlehenshöhen in EUR */
  summe_bestehende_darlehen: number;
  /** True wenn noch kein Darlehen existiert */
  ist_erstes_darlehen: boolean;
}

/** Domäne, der der Gesamtstatus aktuell zuzuordnen ist (für die Badge-Farbe) */
export type GesamtstatusDomain =
  | "neu"
  | "flaechenakquise"
  | "planungsrecht"
  | "bimschg"
  | "eeg"
  | "finanzierung"
  | "bau";

export interface ProjectOverviewItem {
  id: string;
  name: string;
  /** Ob der Nutzer zum Mandanten dieses Projekts gehört – sonst sieht er es über eine Freigabe */
  ist_intern: boolean;
  /** Effektive Bereichsrechte des Nutzers: Bereichsschlüssel -> `lesen` | `schreiben` */
  meine_rechte: Record<string, "lesen" | "schreiben">;
  /** Zentroid der Projektfläche (für Kartenanzeige), null wenn keine Fläche hinterlegt */
  koordinate: Koordinate | null;
  /** Geplante Inbetriebnahme (ISO) – Zieltermin des Projekts in der Übersicht */
  inbetriebnahme: string | null;
  /** Status der Flächenakquise */
  flaechenakquise_status: string;
  /** Status des Planungsrechts */
  planungsrecht_status: string;
  /** Status des BImSchG-Genehmigungsverfahrens */
  bimschg_status: string;
  /** Status der EEG-Ausschreibung */
  eeg_ausschreibung_status: string;
  /** Status der Finanzierung */
  finanzierung_status: string;
  /** Status des Baus */
  bau_status: string;
  /** Abgeleiteter Gesamtstatus (letzter real erreichter Fortschritt entlang der Domänen) */
  gesamtstatus: string;
  /** Domäne des Gesamtstatus (für die Einfärbung des Badges) */
  gesamtstatus_domain: GesamtstatusDomain;
  /** Fortschritt als Zahl zum Sortieren, vom Server nach der Regel; „Neu“ ist 0 */
  gesamtstatus_rang: number;
  /** Letzte Stufe erreicht („In Betrieb“) */
  gesamtstatus_abgeschlossen: boolean;
  /** Summe der Nennleistung aller Anlagen in MW */
  gesamtnennleistung_mw: number | null;
  /** Prozent der gesicherten Projektfläche (Status: Vertrag abgeschlossen) */
  flaechensicherung_prozent: number | null;
  /** P75 Ertragswert in MWh */
  p75_mwh: number | null;
  /** Berechneter Preis pro kWh in Cent (Zuschlagswert × Korrekturfaktor) */
  preis_pro_kwh_cent: number | null;
  /** Gesamte Investitionskosten (CAPEX) in EUR */
  investitionskosten: number | null;
  /** Eigenkapitalquote in Prozent */
  ek_quote: number | null;
  /** Zuschlagswert des EEG-Tarifs in Cent/kWh (ohne Korrekturfaktor) */
  zuschlagswert: number | null;
  /** Nach Darlehenshöhe gewichteter Kreditzins über alle Darlehen in % p.a. */
  kreditzins: number | null;
  /** Internal Rate of Return in Prozent */
  irr: number | null;
  /** Minimaler Debt Service Coverage Ratio */
  min_dscr: number | null;
  /** Durchschnittlicher Debt Service Coverage Ratio */
  avg_dscr: number | null;
  /** Wirtschaftlichkeits-Score (0-100) aus IRR, Investitionsvolumen und EK-Quote */
  wirtschaftlichkeit_score: number | null;
  /** Reifegrad-Score (0-100) aus Fläche, Planungsrecht, Genehmigung, Ausschreibung, Finanzierung und Bau */
  reifegrad_score: number | null;
  /** Anteil gelieferter Gutachten in Prozent (0-100) */
  gutachten_score: number | null;
  /** Score (0-100) aus dem Planungsrecht-Unterstatus */
  planungsrecht_score: number | null;
  /** Score (0-100) aus dem BImSchG-Genehmigungsstatus */
  genehmigung_score: number | null;
  /** Score (0-100) aus Gutachten-Lieferquote und EEG-Ausschreibungsstatus */
  ausschreibung_score: number | null;
  /** Score (0-100) aus dem Finanzierungsstatus */
  finanzierung_score: number | null;
  /** Score (0-100) aus dem Baustatus */
  bau_score: number | null;
}

/** Aktuell konfigurierte Gewichte der 6 Reifegrad-Domänen (org-weit) */
export interface ReifegradGewichte {
  gewicht_flaeche: number;
  gewicht_planungsrecht: number;
  gewicht_genehmigung: number;
  gewicht_ausschreibung: number;
  gewicht_finanzierung: number;
  gewicht_bau: number;
}

/**
 * Response für die Projekt-Übersichtstabelle.
 * Enthält sowohl die Projektliste als auch aggregierte KPIs,
 * die aus den bereits berechneten Übersichtsdaten abgeleitet werden.
 */
export interface ProjectOverviewResponse {
  projects: ProjectOverviewItem[];
  anzahl_projekte: number;
  /** Durchschnittlicher IRR über alle Projekte in Prozent */
  durchschnittlicher_irr: number | null;
  /** Durchschnittliche Eigenkapitalquote in Prozent */
  durchschnittliche_ek_quote: number | null;
  /** Erklärung des Wirtschaftlichkeits-Scores inkl. aktuell konfigurierter Gewichte */
  wirtschaftlichkeit_erklaerung: string;
  /** Erklärung des Reifegrad-Scores inkl. aktuell konfigurierter Gewichte */
  reifegrad_erklaerung: string;
  /** Aktuell konfigurierte Gewichte der 6 Reifegrad-Domänen (org-weit) */
  reifegrad_gewichte: ReifegradGewichte;
}

/** Schweregrad einer Validierung */
export type ValidationSeverity = "error" | "warning" | "info";

/** Domäne, zu der eine Validierung gehört (für Mapping auf PageTypes) */
export type ValidationDomain =
  | "project"
  | "tariffs"
  | "costs"
  | "financing"
  | "yield_forecast"
  | "calculation"
  | "permits";

/**
 * Mapping von Backend-Domain auf Frontend-PageType.
 *
 * Liegt hier (und nicht im PageWarningsContext), damit die Zuordnung zusammen mit
 * `ValidationDomain` gepflegt wird – eine neue Domäne ohne Seitenzuordnung fällt so
 * schon beim Typecheck auf.
 */
export const DOMAIN_TO_PAGE_TYPE: Record<ValidationDomain, PageType> = {
  project: "project-area",
  tariffs: "project-revenues",
  costs: "project-costs",
  financing: "project-financing",
  yield_forecast: "project-yield",
  calculation: "project-calculation",
  permits: "project-permit-applications",
};

/**
 * Referenz auf die betroffene Entität einer Validierung.
 * Ermöglicht dem Frontend, das betroffene Element zu identifizieren.
 */
export interface ValidationEntity {
  /** Typ der Entität (z.B. 'ppa_tariff', 'cost_item') */
  type: string;
  /** Eindeutige ID der Entität */
  id: string;
  /** Anzeigename der Entität (optional) */
  name?: string;
}

/**
 * Einzelner Validierungseintrag mit allen relevanten Informationen.
 * Wird vom Backend bei bestimmten CRUD-Operationen zurückgegeben.
 */
export interface ValidationEntry {
  /** Eindeutiger Code für diese Validierung (z.B. 'PPA_EXCEEDS_PROJECT_RUNTIME') */
  code: string;
  /** Schweregrad der Validierung */
  severity: ValidationSeverity;
  /** Domäne, zu der die Validierung gehört */
  domain: ValidationDomain;
  /** Betroffene Entität */
  entity: ValidationEntity;
  /** Benutzerfreundliche Fehlermeldung auf Deutsch */
  message: string;
  /** Zusätzlicher Kontext für Debugging und detaillierte Tooltips */
  context: Record<string, unknown>;
}

/**
 * Standardisierte Response-Struktur für Validierungen.
 * Wird von dedizierten Action-Endpoints zurückgegeben.
 */
export interface ValidationsResponse {
  /** Liste von Warnungen (Aktion möglich, aber mit Einschränkungen) */
  warnings: ValidationEntry[];
  /** Liste von Fehlern (Aktion möglicherweise nicht möglich) */
  errors: ValidationEntry[];
  /**
   * Domänen, die für diese Response tatsächlich geprüft wurden.
   * Ohne diese Angabe wäre eine leere Liste nicht von "nicht geprüft" zu unterscheiden –
   * nur für die genannten Domänen darf der Warning-Status zurückgesetzt werden.
   */
  domains: ValidationDomain[];
}

/**
 * Response für den PUT /projects/{id}/financing Endpoint.
 * Enthält die aktualisierte Finanzierung und domänenübergreifende Validierungen.
 */
export interface FinancingResponse {
  data: FinancingWithLoans;
  validations: ValidationsResponse;
}

/**
 * Response für den PUT /projects/{id}/laufzeit Endpoint.
 * Enthält das aktualisierte Projekt und domänenübergreifende Validierungen.
 */
export interface ProjectLaufzeitResponse {
  /** Aktualisiertes Projekt */
  data: Windpark;
  /** Domänenübergreifende Validierungen */
  validations: ValidationsResponse;
}

/**
 * Response für den PUT /projects/{id}/aktiver-ertragswert Endpoint.
 * Enthält das aktualisierte Projekt und domänenübergreifende Validierungen.
 */
export interface ProjectAktiverErtragswertResponse {
  /** Aktualisiertes Projekt */
  data: Windpark;
  /** Domänenübergreifende Validierungen */
  validations: ValidationsResponse;
}

/**
 * Response für PPA-CRUD-Operationen mit Validierungen.
 * Enthält den erstellten/aktualisierten PPA und domänenübergreifende Validierungen.
 */
export interface PPATariffResponse {
  /** Erstellter/aktualisierter PPA-Tarif */
  data: PPATariff;
  /** Domänenübergreifende Validierungen */
  validations: ValidationsResponse;
}

/**
 * Response für das Löschen eines PPA-Tarifs.
 * Enthält nur Validierungen (kein data, da der PPA gelöscht wurde).
 */
export interface PPATariffDeleteResponse {
  /** Domänenübergreifende Validierungen nach dem Löschen */
  validations: ValidationsResponse;
}

/** Gesellschafter (Kommanditist) einer Projektgesellschaft */
export interface Shareholder {
  id: string;
  company_id: string;
  name: string;
  anteil_prozent: number;
  ist_eigene_beteiligung: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface ShareholderCreate {
  name: string;
  anteil_prozent: number;
  ist_eigene_beteiligung: boolean;
}

export interface ShareholderUpdate {
  name?: string;
  anteil_prozent?: number;
  ist_eigene_beteiligung?: boolean;
}

/** Projektgesellschaft mit Gesellschaftern */
export interface CompanyWithShareholders {
  id: string;
  project_id: string;
  name: string;
  rechtsform: string;
  komplementaer: string;
  shareholders: Shareholder[];
  created_at: string;
  updated_at: string | null;
}

export interface CompanyUpdate {
  name?: string;
  rechtsform?: string;
  komplementaer?: string;
}

/** Generischer Lookup-Eintrag (procedure_type, evidence_type, requirement_level, ...) */
export interface LookupOption {
  id: string;
  code: string;
  name: string;
  description?: string;
}

/** Nachweisart mit dem Bearbeitungsweg, den sie vorbelegt (`GET /permits/evidence-type-options`) */
export interface EvidenceTypeOption extends LookupOption {
  default_processing_path: ProcessingPath;
}

/** Antragsart mit ihrem Kurznamen – Kurztitel des Antrags in der Navigation */
export interface ProcedureTypeOption extends LookupOption {
  /** z. B. "Vollgenehmigung"; leer bei Antragsarten ohne gepflegten Kurznamen */
  kurzname?: string | null;
}

/** Antragsbaustein-Kategorie inkl. ihrer ELiA-Kapitelnummer */
export interface ApplicationComponentCategoryOption extends LookupOption {
  elia_nummer?: string;
}

/** Antragsbaustein-Typ inkl. seiner übergeordneten Category */
export interface ApplicationComponentTypeOption extends LookupOption {
  elia_nummer?: string;
  category: ApplicationComponentCategoryOption;
}

/**
 * Eintrag im Nachweiskatalog – Auswahl beim Anlegen eines Nachweises. Bezeichnung und
 * Nachweisart gehen unverändert an den neuen Nachweis.
 */
export interface EvidenceCatalogOption {
  id: string;
  code: string;
  bezeichnung: string;
  evidence_type: LookupOption;
  /** Baustein-Typen, in die ein Nachweis dieses Eintrags laut Regel gehört – oben in der Auswahl */
  empfohlene_baustein_typ_ids: string[];
}

/**
 * Womit das Formular für einen neuen Antrag startet – vom Server nach der Regel
 * `domain/permits/regeln/vorbelegung_antrag.py`. Leer heißt: nicht vorbelegt.
 */
export interface AntragVorbelegung {
  /** Nur beim ersten Antrag eines Projekts */
  procedure_type_id: string | null;
  uvp_status_id: string | null;
  vorhabenbestandteil_ids: string[];
  anlage_ids: string[];
  /** Vom zuletzt angelegten Antrag des Projekts */
  windenergiegebiet_id: string | null;
  standortmerkmal_ids: string[];
  genehmigung_geplant_am: string;
}

/**
 * Erwartete Dokumentart eines Antragsbaustein-Typs.
 *
 * Reine Erwartung: daraus entstehen **keine** Nachweiszeilen. Zeigt nur, welche Dokumentarten
 * an diesem Baustein typischerweise hingehören (Regel im Backend unter `domain/permits/regeln/`).
 */
export interface ErwarteteDokumentart {
  evidence_type: LookupOption;
}

/** Genehmigungsverfahren – gehört zu einem Projekt */
/** Eine Windenergieanlage als Auswahl – etwa für den Antragsgegenstand eines Antrags */
export interface AnlageOption {
  id: string;
  bezeichnung: string;
}

/** Antragsstatus (ADR 011) – Schlüssel vom Server, Anzeigenamen ebenso (`status_label`) */
export type ProcedureStatus =
  | "conference_pending"
  | "in_preparation"
  | "finalized"
  | "submitted"
  | "receipt_confirmed"
  | "resubmission_pending"
  | "complete"
  | "draft_decision"
  | "decided"
  | "published"
  | "contested"
  | "final";

/** Die Daten des Antragsstatus: je Status das Ereignis und die Frist (ISO-Daten) */
export interface ProcedureStatusDates {
  conference_planned_on?: string | null;
  /** Leer nach dem Weiterschalten: ohne Antragskonferenz weiter */
  conference_held_on?: string | null;
  finalization_planned_on?: string | null;
  finalized_on?: string | null;
  submission_planned_on?: string | null;
  receipt_expected_on?: string | null;
  receipt_confirmed_on?: string | null;
  completeness_check_due_on?: string | null;
  resubmission_requested_on?: string | null;
  resubmission_due_on?: string | null;
  decision_due_on?: string | null;
  draft_decision_received_on?: string | null;
  publication_expected_on?: string | null;
  published_on?: string | null;
  appeal_deadline_on?: string | null;
  contested_on?: string | null;
  final_on?: string | null;
}

/** Ein Dokument am Antrag – mit dem Antragsstatus, in dem es angehängt wurde */
export interface ProcedureDocument extends Dokument {
  status?: ProcedureStatus | null;
}

/** Vorschlag im Dialog: so lange nach dem Wert eines Feldes */
export interface ProcedureOffset {
  base: string;
  months: number;
  days: number;
}

/** Ein Feld, das eine Aktion am Antrag abfragt */
export interface ProcedureActionField {
  name: string;
  label: string;
  kind: "date" | "documents" | "decision";
  required: boolean;
  /** Leer gelassen, setzt der Server das heutige Datum */
  today_default: boolean;
  /** Der Dialog übernimmt nicht, was schon am Antrag steht */
  fresh: boolean;
  /** Ereignis – liegt nicht in der Zukunft */
  event: boolean;
  /** Frist des Zielstatus */
  deadline: boolean;
  suggest_after?: ProcedureOffset | null;
}

/** Eine Aktion, die im aktuellen Zustand des Antrags zulässig ist */
export interface ProcedureAction {
  action: string;
  label: string;
  target_status?: ProcedureStatus | null;
  comment_required: boolean;
  /** Nur „Status zurücksetzen“: wählbare frühere Status, der nächstliegende zuerst */
  target_options: ProcedureStatus[];
  fields: ProcedureActionField[];
}

export interface ProcedureStatusStep {
  status: ProcedureStatus;
  label: string;
  phase_label: string;
  /** Side branch the procedure is in right now, shown at this station (Nachforderung, Anfechtung) */
  branch_status?: ProcedureStatus | null;
  branch_label?: string | null;
}

export interface ProcedureDeadline {
  label: string;
  start?: string | null;
  due: string;
  field: string;
}

/** Request: eine Aktion am Antrag ausführen */
export interface ProcedureTransition {
  action: string;
  fields?: Partial<ProcedureStatusDates> & {
    eingereicht_am?: string | null;
    vollstaendig_seit?: string | null;
    bescheid_vom?: string | null;
    procedure_decision_id?: string | null;
    documents?: ProcedureDocument[] | null;
  };
  comment?: string | null;
  target_status?: ProcedureStatus | null;
}

export interface PermitProcedure extends ProcedureStatusDates {
  id: string;
  project_id: string;
  procedure_type_id: string;
  procedure_type: ProcedureTypeOption;
  uvp_status_id?: string;
  uvp_status?: LookupOption;
  /** Antragsstatus (ADR 011) samt Anzeige */
  status: ProcedureStatus;
  status_label: string;
  phase_label: string;
  /** Kennzeichen Zurückgenommen */
  withdrawn: boolean;
  status_steps: ProcedureStatusStep[];
  /** Completed and total not archived evidences – the count of the procedure progress */
  evidences_completed: number;
  evidences_total: number;
  documents: ProcedureDocument[];
  /** Zulässige Aktionen, der nächste Schritt zuerst */
  actions: ProcedureAction[];
  /** Bezeichnung und Feld der Frist des aktuellen Status – auch solange sie leer ist */
  deadline_label?: string | null;
  deadline_field?: string | null;
  deadline?: ProcedureDeadline | null;
  procedure_decision_id?: string;
  procedure_decision?: LookupOption;
  /** Bestandteile neben den Windenergieanlagen; leer = noch nicht erfasst */
  vorhabenbestandteile: LookupOption[];
  /** Antragsgegenstand – IDs der Windenergieanlagen, die der Antrag umfasst; leer = unvollständig */
  anlage_ids: string[];
  bezeichnung?: string;
  beschreibung?: string;
  /** Benutzer, der den Antrag bearbeitet – Termine des Antrags hängen daran.
   *  IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`), nicht die interne ID */
  bearbeitung_user_id: string;
  bearbeitung: EvidenceUser;
  /** Eingang bei der Behörde (ISO) – Beginn der Monatsfrist zur Vollständigkeitsprüfung */
  eingereicht_am?: string | null;
  /** Feststellung der Vollständigkeit (ISO) – Beginn der Entscheidungsfrist */
  vollstaendig_seit?: string | null;
  /** Ab wann die abgeleiteten Angaben festgeschrieben sind – gesetzt mit der Einreichung */
  werte_eingefroren_am?: string | null;
  /** Behördliches Aktenzeichen dieses Antrags – vergeben mit dem Eingang bei der Behörde */
  aktenzeichen?: string | null;
  /** Datum des Genehmigungsbescheids (ISO, yyyy-MM-dd) */
  bescheid_vom?: string | null;
  /** Geplantes Genehmigungsdatum (ISO) – rechte Grenze des Zeitstrahls der Nachweise */
  genehmigung_geplant_am?: string | null;
  /** Antrag in strømcraft, auf den dieser Bezug nimmt – auch aus einem anderen Projekt */
  bezugsgenehmigung_id?: string | null;
  /** Behördliches Aktenzeichen des Bezugsbescheids */
  bezugsgenehmigung_aktenzeichen?: string | null;
  /** Datum des Bezugsbescheids (ISO, yyyy-MM-dd) */
  bezugsgenehmigung_bescheid_vom?: string | null;
  /** Freitext zum Zusatzantrag (§ 8 Teilgenehmigung bzw. § 8a vorzeitiger Beginn) */
  zusatzantrag_bemerkung?: string | null;
  document_url?: string;
  /** Lage zum Windenergiegebiet; leer = noch nicht erfasst */
  windenergiegebiet_id?: string | null;
  windenergiegebiet?: WindenergiegebietOption | null;
  /** Besonderheiten des Standorts, sortiert wie die Auswahlliste */
  standortmerkmale: StandortmerkmalOption[];
  /** Anlagezeitpunkt (ISO) – der zuletzt angelegte Antrag belegt den Standort eines neuen vor */
  created_at: string;
}

/** Ein Beteiligter im Genehmigungsantrag – Antragsteller, Rückfragen, § 52b, Verfasser */
export interface PermitKontakt {
  id: string;
  permit_procedure_id: string;
  kontakt_rolle_id: string;
  kontakt_rolle: LookupOption;
  /** Benutzer, aus dem Name, E-Mail, Telefon und Fax stammen.
   *  IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`), nicht die interne ID */
  user_id?: string | null;
  /** Projektgesellschaft, aus der die Firmenangaben stammen */
  company_id?: string | null;
  /** Gesellschaft des Mandanten, aus der die Firmenangaben stammen */
  org_gesellschaft_id?: string | null;
  /** Person kommt aus einem Benutzer und ist am Kontakt nicht änderbar */
  person_aus_benutzer: boolean;
  /** Firma kommt aus einer Gesellschaft und ist am Kontakt nicht änderbar */
  firma_aus_gesellschaft: boolean;
  vorname?: string | null;
  nachname?: string | null;
  telefon?: string | null;
  fax?: string | null;
  email?: string | null;
  firma?: string | null;
  rechtsform?: string | null;
  strasse?: string | null;
  hausnummer?: string | null;
  plz?: string | null;
  ort?: string | null;
}

/** Angaben eines Kontakts ohne die Rolle, die ihn identifiziert */
export interface PermitKontaktFelder {
  /** IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`), nicht die interne ID */
  user_id?: string | null;
  company_id?: string | null;
  org_gesellschaft_id?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  telefon?: string | null;
  fax?: string | null;
  email?: string | null;
  firma?: string | null;
  rechtsform?: string | null;
  strasse?: string | null;
  hausnummer?: string | null;
  plz?: string | null;
  ort?: string | null;
}

export interface PermitKontaktCreate extends PermitKontaktFelder {
  kontakt_rolle_id: string;
}

/**
 * Ein Gutachter bzw. eine Institution des Mandanten als Vorschlag – abgeleitet aus den
 * Nachweisen
 */
export interface EvidenceExpert {
  /** In der zuletzt verwendeten Schreibweise */
  name: string;
  /** Letzte Änderung eines seiner Nachweise (ISO) */
  last_used_at: string;
  /** Bisher verwendete E-Mail-Adressen, neueste zuerst – die erste wird vorgeschlagen */
  emails: string[];
  /** Nachweisarten, für die er bisher verwendet wurde */
  evidence_type_ids: string[];
}

/** Bearbeitungsweg eines Nachweises: Selbst erledigen, Anfordern, Beauftragen */
export type ProcessingPath = "self" | "request" | "commission";

/** Ein Bearbeitungsweg zur Wahl – mit seinem ersten Schritt, solange der Nachweis offen ist */
export interface EvidencePathOption {
  value: ProcessingPath;
  label: string;
  /** Einzeiler, was der Weg bedeutet – Tooltip am Status */
  description: string;
  start_action?: EvidenceAktion | null;
}

/** Ein Bearbeitungsweg mit Namen und Einzeiler (`GET /permits/processing-path-options`) */
export interface ProcessingPathOption {
  value: ProcessingPath;
  label: string;
  description: string;
}

/** Anfordern von: intern ein Benutzer (IAM-ID) oder extern eine Institution mit E-Mail */
export interface EvidenceRequestedFrom {
  user_id?: string | null;
  institution?: string | null;
  email?: string | null;
}

/** Ein Antrag als Auswahlmöglichkeit für die Bezugsgenehmigung */
/** UVP-Status mit seiner Gruppe – Überschrift im Dropdown */
export interface UvpStatusOption extends LookupOption {
  /** "UVP-Pflicht" oder "Ergebnis der Vorprüfung" */
  gruppe?: string | null;
}

export interface PermitProcedureReference {
  id: string;
  project_id: string;
  project_name: string;
  bezeichnung?: string | null;
  antragsart: string;
  /** Behördliches Aktenzeichen – leer, solange der Antrag nicht eingereicht ist */
  aktenzeichen?: string | null;
  /** Datum seines Genehmigungsbescheids (ISO, yyyy-MM-dd) */
  bescheid_vom?: string | null;
}

/**
 * Daten zum Erstellen eines Genehmigungsverfahrens.
 *
 * Der Server legt keine Antragsbausteine mit an – ein neuer Antrag ist leer.
 */
export interface PermitProcedureCreate {
  procedure_type_id: string;
  uvp_status_id?: string | null;
  procedure_decision_id?: string | null;
  /** Antragskonferenz am (ISO) – Frist des ersten Antragsstatus, freiwillig */
  conference_planned_on?: string | null;
  /** Bestandteile neben den Windenergieanlagen; leere Liste = noch nicht erfasst */
  vorhabenbestandteil_ids?: string[];
  /** Antragsgegenstand; weggelassen = alle Anlagen des Projekts */
  anlage_ids?: string[];
  bezeichnung?: string;
  beschreibung?: string;
  /** IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`); weggelassen = der Anlegende */
  bearbeitung_user_id?: string | null;
  aktenzeichen?: string | null;
  /** Geplantes Genehmigungsdatum (ISO) – rechte Grenze des Zeitstrahls der Nachweise */
  genehmigung_geplant_am?: string | null;
  bezugsgenehmigung_id?: string | null;
  windenergiegebiet_id?: string | null;
  /** Besonderheiten des Standorts; leere Liste = keine trifft zu */
  standortmerkmal_ids?: string[];
  document_url?: string;
}
export interface PermitProcedureUpdate extends ProcedureStatusDates {
  procedure_type_id?: string | null;
  uvp_status_id?: string | null;
  /** Ersetzt die Dokumente am Antrag vollständig; weggelassen = unverändert */
  documents?: ProcedureDocument[];
  procedure_decision_id?: string | null;
  /** Ersetzt die Zuordnung vollständig (kein Merge); weggelassen = unverändert */
  vorhabenbestandteil_ids?: string[];
  /** Ersetzt den Antragsgegenstand vollständig (kein Merge); weggelassen = unverändert */
  anlage_ids?: string[];
  bezeichnung?: string | null;
  beschreibung?: string | null;
  /** IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`), nicht die interne ID */
  bearbeitung_user_id?: string | null;
  eingereicht_am?: string | null;
  vollstaendig_seit?: string | null;
  aktenzeichen?: string | null;
  bescheid_vom?: string | null;
  genehmigung_geplant_am?: string | null;
  bezugsgenehmigung_id?: string | null;
  windenergiegebiet_id?: string | null;
  /** Ersetzt die Besonderheiten des Standorts vollständig (kein Merge); weggelassen = unverändert */
  standortmerkmal_ids?: string[];
  zusatzantrag_bemerkung?: string | null;
  document_url?: string | null;
}

/** Antragsbaustein – gehört zu genau einem Genehmigungsverfahren */
export interface ApplicationComponent {
  id: string;
  permit_procedure_id: string;
  application_component_type_id: string;
  application_component_type: ApplicationComponentTypeOption;
  /** Gehört der Baustein in den Antrag? Deaktivierte zählen nicht in den Fortschritt */
  ist_aktiv: boolean;
  /**
   * True, wenn der Nutzer den Zustand selbst gesetzt oder den Baustein von Hand ergänzt hat.
   * Wird derzeit nirgends ausgewertet.
   */
  ist_aktiv_manuell: boolean;
  /** Erwartete Dokumentarten des Baustein-Typs (nur aktive Zuordnungen) */
  erwartete_dokumentarten: ErwarteteDokumentart[];
  /**
   * Abgeleiteter Fortschritt in Prozent. Nenner ist `max(angelegte Nachweise, erwartete
   * Dokumentarten)` – wird ausschließlich im Backend gerechnet, das Frontend zeigt ihn nur an.
   */
  fortschritt: number;
  fortschritt_label: string;
  inhalt?: string;
  document_url?: string;
}

export interface ApplicationComponentCreate {
  application_component_type_id: string;
  ist_aktiv?: boolean;
  inhalt?: string | null;
  document_url?: string;
}

/**
 * Daten zum Aktualisieren eines Antragsbausteins.
 *
 * `ist_aktiv_manuell` fehlt bewusst: der Server setzt das Kennzeichen selbst, sobald ein Update
 * den Zustand tatsächlich ändert.
 */
export interface ApplicationComponentUpdate {
  application_component_type_id?: string | null;
  ist_aktiv?: boolean;
  inhalt?: string | null;
  document_url?: string | null;
}

/** Kompakte User-Darstellung innerhalb eines Nachweises */
export interface EvidenceUser {
  id: string;
  external_id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
}

/**
 * Nachweisstatus – nur über Aktionen änderbar (ADR 007). Reihenfolge des Ablaufs siehe
 * `EVIDENCE_STATUS_ORDER`.
 */
export type EvidenceStatus =
  | "Offen"
  | "Erledigt"
  | "Angefordert"
  | "Angebot angefragt"
  | "Angebot erhalten"
  | "Beauftragt"
  | "In Korrektur"
  | "Geliefert"
  | "Freigegeben";

/** Ein Feld, das eine Aktion im Dialog abfragt */
export interface EvidenceAktionFeld {
  /** Feldname am Nachweis, z. B. "lieferung_bis" */
  name: EvidenceUebergangFeldName;
  label: string;
  /** Wie der Dialog das Feld zeigt */
  typ:
    | "datum"
    | "text"
    | "email"
    | "notiz"
    | "dokument_liste"
    | "requested_from";
  pflicht: boolean;
  /** Leer gelassen, setzt der Server das heutige Datum */
  heute_vorbelegt: boolean;
  /** Vorbelegung im Dialog: heute plus so viele Tage; der Server setzt nichts */
  vorschlag_in_tagen?: number | null;
  /** Der Dialog übernimmt den Wert, der schon am Nachweis steht */
  bestandswert_vorbelegen?: boolean;
  /**
   * Der Dialog übernimmt den Wert dieses Feldes, solange man es nicht selbst ändert – aus dem
   * Dialog, wenn er es mit abfragt, sonst vom Nachweis
   */
  vorbelegt_aus?: EvidenceUebergangFeldName | null;
}

/** Eine Aktion, die im aktuellen Zustand des Nachweises zulässig ist – vom Server */
export interface EvidenceAktion {
  /** Schlüssel, z. B. "angebot_anfragen" */
  aktion: string;
  label: string;
  /** Status nach der Aktion; fehlt, wenn sie ihn nicht ändert oder er wählbar ist */
  ziel_status?: EvidenceStatus | null;
  kommentar_pflicht: boolean;
  /** Nur „Status zurücksetzen": wählbare frühere Status, der nächstliegende zuerst */
  ziel_optionen: EvidenceStatus[];
  felder: EvidenceAktionFeld[];
  /** Betrag der Kostenposition, den die Aktion abfragt – zeigen nur mit Recht an den Kosten */
  cost_field?: EvidenceActionCostField | null;
}

/** Ein Betrag der Kostenposition des Nachweises, den eine Aktion abfragt – immer freiwillig */
export interface EvidenceActionCostField {
  name: EvidenceCostFieldName;
  label: string;
  /** Vorbelegung, solange der Betrag selbst noch fehlt; der Server setzt nichts */
  prefill_from?: EvidenceCostFieldName | null;
}

/** Plankosten bzw. Istkosten der Kostenposition */
export type EvidenceCostFieldName = "betrag_geplant" | "betrag_ist";

/**
 * Ein Dokument: Link ins Dokumentenmanagement, sein Anzeigename und optional sein
 * Ausstellungsdatum. Ein leerer Name wird vom Server aus der URL abgeleitet (Dateiname ohne
 * Endung).
 */
export interface Dokument {
  url: string;
  name: string;
  /** Ausgestellt am (ISO-Datum, nicht in der Zukunft); fehlt an älteren Dokumenten */
  issued_on?: string | null;
}

/** Felder, die eine Aktion setzen kann */
export type EvidenceUebergangFeldName =
  | "gutachter"
  | "expert_email"
  | "requested_from"
  | "requested_at"
  | "completed_at"
  | "note"
  | "dokumente"
  | "angefragt_am"
  | "erinnerung_am"
  | "angebot_erhalten_am"
  | "beauftragung_erinnerung_am"
  | "offer_valid_until"
  | "beauftragt_am"
  | "lieferung_bis"
  | "geliefert_am"
  | "pruefung_bis"
  | "freigegeben_am"
  | "report_valid_until";

/** Request: eine Aktion am Nachweis ausführen */
export interface EvidenceUebergang {
  aktion: string;
  felder?: Partial<
    Record<
      EvidenceUebergangFeldName,
      string | Dokument[] | EvidenceRequestedFrom | null
    >
  >;
  kommentar?: string | null;
  ziel_status?: EvidenceStatus | null;
  /** Neuer Bearbeitungsweg: für „change_path“, aus „Offen“ auch mit dem ersten Schritt eines anderen Wegs */
  processing_path?: ProcessingPath | null;
  /** Beträge der Kostenposition; mitgeschickt und leer heißt leeren */
  costs?: Partial<Record<EvidenceCostFieldName, number | null>>;
}

/** Ein Eintrag der Nachweishistorie, neueste zuerst */
export interface EvidenceHistorieEintrag {
  id: string;
  /** Aktion, "felder_geaendert" oder "uebernommen" */
  aktion: string;
  aktion_label: string;
  /** Leer, wenn der Eintrag den Status nicht geändert hat */
  status_von?: EvidenceStatus | null;
  status_nach?: EvidenceStatus | null;
  /** Geänderte Workflow-Felder; Datumswerte als ISO-Text */
  aenderungen: {
    feld: string;
    label: string;
    alt?: string | null;
    neu?: string | null;
    /** Beträge ohne Recht an den Kosten weggelassen – nur dass sie sich geändert haben */
    hidden?: boolean;
  }[];
  kommentar?: string | null;
  benutzer?: string | null;
  erstellt_am: string;
}

/** Ein Eintrag der Antragshistorie – Werte als Anzeigetext, Datum als ISO */
export interface PermitProcedureHistoryEntry {
  id: string;
  /** „created“, „changed“ oder ein Ereignis einer manuellen Frist */
  action: string;
  action_label: string;
  changes: {
    field: string;
    label: string;
    old?: string | null;
    new?: string | null;
  }[];
  comment?: string | null;
  /** Anzeigename der handelnden Person */
  user?: string | null;
  created_at: string;
}

/**
 * Nachweis – ein konkretes Exemplar am Antrag, z. B. das Schallgutachten für den Antrag Uppen.
 *
 * Gehört genau einem Antrag und kann von keinem, einem oder mehreren Antragsbausteinen
 * benötigt werden (ADR 006). `application_components` nennt diese Bausteine.
 */
/** Ein Datumsfeld des Nachweis-Details nach der Termin-Regel des Servers */
export interface EvidenceTerminfeld {
  name: string;
  label: string;
  /** Gehört zum aktuellen Status; sonst ein Termin früherer Status (aufklappbar) */
  aktuell: boolean;
  /** Darf nicht vor diesem Feld liegen */
  nicht_vor: string | null;
  nicht_vor_label: string | null;
  /** Ereignis – liegt nicht in der Zukunft */
  ereignis: boolean;
  /** Frist – steht in der Tabelle „Termine“; sonst in der Kachel „Nachweis“ */
  is_deadline: boolean;
}

export interface Evidence {
  id: string;
  permit_procedure_id: string;
  /** Antragsbausteine, die diesen Nachweis benötigen – vom Server in ELiA-Reihenfolge */
  /**
   * Datumsfelder für den aktuellen Status, aktuelle zuerst – vom Server nach der Termin-Regel
   * (`domain/permits/regeln/evidence_status.py`). Das Detail zeigt und prüft genau diese.
   */
  terminfelder: EvidenceTerminfeld[];
  /** Ab „Geliefert“: es gibt ein Dokument mit Ergebnissen und Auflagen */
  dokument_geliefert: boolean;
  application_components: EvidenceComponentRef[];
  evidence_type_id: string;
  evidence_type: LookupOption;
  /** Katalogeintrag, aus dem der Nachweis entstand – leer bei „Sonstige“ */
  evidence_catalog_entry_id?: string | null;
  /** Bezeichnung des konkreten Nachweises – unterscheidet mehrere Nachweise derselben Nachweisart */
  bezeichnung?: string;
  /** Bearbeitungsweg – nur über Aktionen änderbar */
  processing_path: ProcessingPath;
  /** Die Status des Wegs in Reihenfolge – der Statusstrahl */
  status_steps: EvidenceStatus[];
  /** Alle Wege, der eigene zuerst; solange offen je Weg sein erster Schritt */
  path_options: EvidencePathOption[];
  /** Endstatus des Wegs erreicht */
  completed: boolean;
  /** Kennzeichen Problem – beschrieben in der Notiz */
  problem: boolean;
  gutachter?: string;
  expert_email?: string | null;
  /** Anfordern von, intern: IAM-ID aus `usersApi.getOrgUsers()` */
  requested_from_user_id?: string | null;
  requested_from_user?: EvidenceUser | null;
  /** Anfordern von, extern */
  institution?: string | null;
  requested_from_email?: string | null;
  requested_at?: string | null;
  completed_at?: string | null;
  beauftragt_am?: string;
  lieferung_bis?: string;
  geliefert_am?: string;
  /** IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`), nicht die interne ID */
  verantwortlich_id?: string;
  verantwortlich?: EvidenceUser;
  angefragt_am?: string;
  erinnerung_am?: string;
  angebot_erhalten_am?: string;
  beauftragung_erinnerung_am?: string;
  offer_valid_until?: string;
  pruefung_bis?: string;
  freigegeben_am?: string;
  status: EvidenceStatus;
  /** Nicht mehr benötigt – zählt nicht in den Antragsfortschritt */
  archiviert: boolean;
  /** Im aktuellen Zustand zulässige Aktionen, der nächste Schritt zuerst */
  aktionen: EvidenceAktion[];
  report_valid_until?: string;
  /** Notiz zum aktuellen Stand */
  note?: string | null;
  /** Dokumente im Dokumentenmanagement; leer = noch kein Dokument */
  dokumente: Dokument[];
  /** Termin des aktuellen Status, vom Server nach derselben Regel wie die Terminübersicht */
  offene_frist?: EvidenceFrist | null;
  /** Wo die Frist des Status steht; leer bei offenen, freigegebenen und archivierten */
  fristfeld?: EvidenceFristfeld | null;
}

/** Datumsfelder, die im jeweiligen Status die Frist tragen */
export type EvidenceFristfeldName =
  | "erinnerung_am"
  | "beauftragung_erinnerung_am"
  | "lieferung_bis"
  | "pruefung_bis";

/** Feld der Frist im aktuellen Status – auch solange es noch leer ist */
export interface EvidenceFristfeld {
  feld: EvidenceFristfeldName;
  anlass: string;
}

/** Offene Frist eines Nachweises, z. B. { anlass: "Lieferung bis", zieltermin: "2026-12-01" } */
export interface EvidenceFrist {
  anlass: string;
  zieltermin: string;
}

/** Antragsbaustein, der einen Nachweis benötigt */
export interface EvidenceComponentRef {
  id: string;
  application_component_type_id: string;
  /** Formularnummer in ELiA, z. B. "4.6" */
  elia_nummer?: string;
  /** Name des Antragsbaustein-Typs */
  name: string;
}

/** Ein Nachweis in der antragsübergreifenden Nachweisliste */
export interface ProjectEvidence extends Evidence {
  /** Anzeigename des Antrags – vom Server aufgelöst, fällt auf die Antragsart zurück */
  permit_procedure_bezeichnung: string;
}

/** Daten zum Erstellen eines Nachweises */
export interface EvidenceCreate {
  /** Katalogeintrag, aus dem der Nachweis entsteht – leer bei „Sonstige“ */
  evidence_catalog_entry_id?: string;
  /**
   * Optional: Baustein-Typen, deren Bausteine den Nachweis benötigen. Ein Typ, der noch nicht im
   * Antrag ist, nimmt der Server dabei als Baustein auf.
   */
  application_component_type_ids?: string[];
  evidence_type_id: string;
  /** Ohne Angabe der vorbelegte Weg der Nachweisart */
  processing_path?: ProcessingPath;
  bezeichnung?: string;
  dokumente?: Dokument[];
  gutachter?: string;
  beauftragt_am?: string;
  lieferung_bis?: string;
  geliefert_am?: string;
  /** IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`), nicht die interne ID */
  verantwortlich_id?: string;
  angefragt_am?: string;
  erinnerung_am?: string;
  angebot_erhalten_am?: string;
  beauftragung_erinnerung_am?: string;
  offer_valid_until?: string;
  pruefung_bis?: string;
  freigegeben_am?: string;
  report_valid_until?: string;
  /** Notiz zum aktuellen Stand */
  note?: string | null;
}

/**
 * Daten zum Aktualisieren eines Nachweises.
 *
 * Der Antragsbaustein fehlt: er steht seit dem Anlegen fest, weil sich das Fachthema aus ihm
 * ergibt. An einem Verweis wirkt gar nichts – alles ist geerbt und wird serverseitig verworfen.
 */
export interface EvidenceUpdate {
  /** Ersetzt die Zuordnung zu Antragsbausteinen; fehlende Bausteine nimmt der Server auf */
  application_component_type_ids?: string[];
  evidence_type_id?: string | null;
  bezeichnung?: string | null;
  /** Ersetzt die ganze Liste */
  dokumente?: Dokument[] | null;
  gutachter?: string | null;
  expert_email?: string | null;
  /** Anfordern von, intern – leert die Institution */
  requested_from_user_id?: string | null;
  /** Anfordern von, extern – leert den Benutzer */
  institution?: string | null;
  requested_from_email?: string | null;
  requested_at?: string | null;
  completed_at?: string | null;
  beauftragt_am?: string | null;
  lieferung_bis?: string | null;
  geliefert_am?: string | null;
  /** IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`), nicht die interne ID */
  verantwortlich_id?: string | null;
  angefragt_am?: string | null;
  erinnerung_am?: string | null;
  angebot_erhalten_am?: string | null;
  beauftragung_erinnerung_am?: string | null;
  offer_valid_until?: string | null;
  pruefung_bis?: string | null;
  freigegeben_am?: string | null;
  report_valid_until?: string | null;
  /** Notiz zum aktuellen Stand */
  note?: string | null;
}

/**
 * Kostenposition eines Genehmigungsverfahrens.
 *
 * Hängt immer am Verfahren und optional zusätzlich an einem Nachweis, an jedem höchstens eine.
 * `betrag_geplant` sind die Plankosten, `betrag_ist` die Istkosten: was tatsächlich angefallen ist.
 */
export interface PermitCostItem {
  id: string;
  permit_procedure_id: string;
  /** Zugehöriger Nachweis; leer bei Kosten ohne Dokumentbezug (z.B. Behördengebühren) */
  evidence_id?: string;
  /** Eigene Bezeichnung – nur gesetzt (und pflichtig) bei Positionen ohne Nachweisbezug */
  bezeichnung?: string;
  /**
   * Fertiger Anzeigename vom Server: eigene Bezeichnung, sonst die des Nachweises.
   * Immer diesen verwenden – die Fallback-Kette gehört nicht ins Frontend.
   */
  anzeige_bezeichnung: string;
  /** Beide optional: "nicht gepflegt" ist von "kostet nichts" unterscheidbar */
  betrag_geplant?: number;
  betrag_ist?: number;
  created_at: string;
  updated_at?: string;
}

/** Eine Kostenposition in der antragsübergreifenden Kostenliste */
export interface ProjectPermitCostItem extends PermitCostItem {
  /** Anzeigename des Antrags – vom Server aufgelöst, fällt auf die Antragsart zurück */
  permit_procedure_bezeichnung: string;
}

export interface PermitCostItemCreate {
  /** Pflicht nur ohne `evidence_id` – mit Nachweisbezug ist der Nachweis der Name */
  bezeichnung?: string | null;
  evidence_id?: string | null;
  betrag_geplant?: number | null;
  betrag_ist?: number | null;
}

export interface PermitCostItemUpdate {
  bezeichnung?: string;
  evidence_id?: string | null;
  betrag_geplant?: number | null;
  betrag_ist?: number | null;
}

/**
 * Kostenübersicht eines Genehmigungsverfahrens.
 *
 * `summe_geplant` ist bewusst die Summe der **erfassten** Positionen, nicht die des Verfahrens –
 * deshalb in der UI als "Erfasste Plankosten" beschriften, nicht als "Gesamtkosten".
 */
export interface PermitCostSummary {
  summe_geplant: number;
  summe_ist: number;
  anzahl_positionen: number;
}

/** Labels für Nachweis-Status */
export const EVIDENCE_STATUS_LABELS: Record<EvidenceStatus, string> = {
  Offen: "Offen",
  Erledigt: "Erledigt",
  Angefordert: "Angefordert",
  "Angebot angefragt": "Angebot angefragt",
  "Angebot erhalten": "Angebot erhalten",
  Beauftragt: "Beauftragt",
  "In Korrektur": "In Korrektur",
  Geliefert: "Geliefert",
  Freigegeben: "Freigegeben",
};

/**
 * Alle Status in einer festen Reihenfolge – zum Sortieren und für Filter. Den Ablauf eines
 * Nachweises liefert der Server je Bearbeitungsweg (`Evidence.status_steps`).
 */
export const EVIDENCE_STATUS_ORDER: EvidenceStatus[] = [
  "Offen",
  "Angefordert",
  "Angebot angefragt",
  "Angebot erhalten",
  "Beauftragt",
  "In Korrektur",
  "Geliefert",
  "Freigegeben",
  "Erledigt",
];

// Standort eines Genehmigungsantrags

/** Auswählbare Standortbesonderheit (Options-Endpunkt, global); alle frei kombinierbar */
export interface StandortmerkmalOption {
  id: string;
  code: string;
  name: string;
  description?: string;
}

/** Auswählbare Lage zum Windenergiegebiet (Options-Endpunkt, global); einwertig */
export interface WindenergiegebietOption {
  id: string;
  code: string;
  name: string;
  description?: string;
}

/** Kompakte User-Darstellung innerhalb eines Termins */
export interface DeadlineUser {
  id: string;
  external_id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
}

/**
 * Bezug einer Frist: woran sie hängt und wohin ein Klick führt.
 * `personal`: persönlicher Termin ohne Projekt – er gehört dem Verantwortlichen.
 */
export type DeadlineReferenceType =
  | "project"
  | "permit_procedure"
  | "evidence"
  | "personal";

/** Problemsignale eines Projekts */
export interface ProjectSignals {
  project_id: string;
  /** Offene Fristen, heute oder früher fällig */
  overdue: number;
  /** Nachweise und offene manuelle Fristen mit dem Kennzeichen Problem */
  problems: number;
}

/** Ein Antrag in der Zusammenfassung der aufgeklappten Projektzeile */
export interface PermitProcedureSummary {
  id: string;
  label: string;
  procedure_type: string;
  /** Kurzname der Antragsart wie in der Sidebar; leer, wenn keiner gepflegt ist */
  procedure_type_short: string | null;
  /** Abgeschlossene Nachweise (Endstatus ihres Bearbeitungswegs), ohne archivierte */
  evidences_completed: number;
  /** Alle nicht archivierten Nachweise */
  evidences_total: number;
  genehmigung_geplant_am: string | null;
  bescheid_vom: string | null;
  /** Wer den Antrag bearbeitet – IAM-ID aus `usersApi.getOrgUsers()` (`OrgUser.user_id`) */
  bearbeitung_user_id: string;
  bearbeitung_name: string;
  /** Summe der geplanten Kosten; leer, solange keine Position einen Betrag hat */
  costs_planned: number | null;
  /** Summe der bisher angefallenen Kosten; leer, solange keine Position einen hat */
  costs_actual: number | null;
}

/** Die Anträge eines Projekts, ältester zuerst */
export interface PermitSummary {
  procedures: PermitProcedureSummary[];
}

/**
 * Einzelne Frist – in der UI „Termin". Abgeleitete Fristen entstehen aus dem Fachstand, manuelle
 * legt jemand von Hand an und hakt sie ab (ADR 010).
 */
export interface DeadlineItem {
  id: string;
  /** Leer beim persönlichen Termin */
  project_id?: string | null;
  bezeichnung: string;
  project_name?: string | null;
  beauftragt_am?: string;
  zieltermin?: string;
  verantwortlich?: DeadlineUser;
  source: "derived" | "manual";
  reference_type: DeadlineReferenceType;
  /** Antrag des Bezugs – auch bei Bezug Nachweis gesetzt */
  permit_procedure_id?: string | null;
  evidence_id?: string | null;
  /** Name von Nachweis bzw. Antrag des Bezugs */
  reference_label?: string | null;
  /** Name des Antrags des Bezugs – auch bei Bezug Nachweis; für den Pfad zum Bezug */
  permit_procedure_label?: string | null;
  /** Nur abgeleitete Frist: das Feld am Nachweis, das den Zieltermin trägt */
  due_field?: string | null;
  note?: string | null;
  /** Abgehakt am; nur bei manuellen Fristen */
  done_at?: string | null;
  /** Kennzeichen Problem: der manuellen Frist selbst, bei einer Frist des Nachweises dessen */
  problem?: boolean;
}

/** Einen persönlichen Termin anlegen oder ändern – verantwortlich ist man selbst */
export interface PersonalDeadlineInput {
  title: string;
  due_date: string;
  note?: string | null;
  /** Kennzeichen Problem – nur mit Notiz */
  problem?: boolean;
}

/** Was der Dialog „Termin anlegen" speichert: ein persönlicher Termin oder ein Projekttermin */
export type DeadlineSubmission =
  | { kind: "personal"; data: PersonalDeadlineInput }
  | { kind: "project"; projectId: string; data: ManualDeadlineInput };

/** Eine manuelle Frist anlegen oder ändern. Ohne Antrag und Nachweis hängt sie am Projekt. */
export interface ManualDeadlineInput {
  title: string;
  due_date: string;
  /** IAM-ID aus der Benutzerauswahl */
  responsible_user_id?: string | null;
  note?: string | null;
  /** Kennzeichen Problem – nur mit Notiz */
  problem?: boolean;
  permit_procedure_id?: string | null;
  evidence_id?: string | null;
}

export * from "./zugriff";

/**
 * Eingangsgrößen, auf die alle Projekte der Übersicht zum Vergleich gestellt werden.
 * Ein leeres Feld heißt: Wert des jeweiligen Projekts beibehalten.
 */
export interface WirtschaftlichkeitSimulationRequest {
  /** Zuschlagswert in Cent/kWh */
  zuschlagswert?: number;
  /** Kreditzins in % p.a. */
  fremdkapitalzins?: number;
}

/** Ergebnis einer Was-wäre-wenn-Rechnung für genau ein Projekt. Wird nie gespeichert. */
export interface WirtschaftlichkeitSimulationResponse {
  /** Simulierter Eigenkapital-IRR in Prozent */
  irr: number | null;
  /** Simulierter Wirtschaftlichkeits-Score (0-100) */
  wirtschaftlichkeit_score: number | null;
  /** Gesetzt, wenn das Projekt nicht simuliert werden konnte – dann gilt weiter der gespeicherte Wert */
  nicht_simulierbar_grund: string | null;
}

/** Gesamtstatus eines Projekts – vom Server (Regel `domain/projects/regeln/gesamtstatus.py`) */
export interface Gesamtstatus {
  gesamtstatus: string;
  gesamtstatus_domain: GesamtstatusDomain;
  gesamtstatus_abgeschlossen: boolean;
}
